import { useEffect, useRef, useState, useId } from 'react';
import { useQuery } from '@tanstack/react-query';
import { loadKakaoMap } from '@/lib/kakaoMap';
import { formatDistance, searchNearby, searchPlaces } from './api';
import type { PlaceResult } from './api';
import { Icon } from '@/shared/ui/Icon';
import { LoadingSpinner } from '@/shared/ui/LoadingSpinner';
import { ErrorState } from '@/shared/ui/ErrorState';
import { Input } from '@/shared/ui/Input';
import { StarRating } from './StarRating';
import { useToast } from '@/shared/ui/Toast';

interface ReferencePoint {
  name: string;
  x: number;
  y: number;
}
interface PlaceMapViewProps {
  places?: PlaceResult[];
  referencePoint?: ReferencePoint | null;
  variant?: 'inline' | 'fullscreen';
  selectedPlaceId?: string | null;
  onSelect: (place: PlaceResult) => void;
}

const SEOUL_CENTER = { x: 126.978, y: 37.5665 };
// 지도 위 번호 마커와 리스트 번호가 1:1 — 카카오맵 검색 결과 문법
const MAX_RESULTS = 15;
const QUICK_SEARCH_CHIPS = [
  { label: '음식점', category: 'FD6' },
  { label: '카페', category: 'CE7' },
  { label: '놀거리', keyword: '놀거리' },
  { label: '술집', keyword: '술집' },
  { label: '맛집', keyword: '맛집' },
  { label: '디저트', keyword: '디저트' },
] as const;

// 카카오 지도 타일은 항상 라이트라 마커 색은 앱 테마를 따라가면 안 된다
// (다크 토큰을 쓰면 밝은 지도 위 밝은 핀이 됨) — 라이트 팔레트 값으로 고정.
const MARKER_FILL = '#45bd58';
const MARKER_FILL_SELECTED = '#1f5429';

function markerImage(n: number, selected: boolean): kakao.maps.MarkerImage {
  const fill = selected ? MARKER_FILL_SELECTED : MARKER_FILL;
  const w = selected ? 34 : 28;
  const h = selected ? 42 : 35;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 28 35">
    <path d="M14 .8C6.8.8 1 6.6 1 13.8c0 9.4 13 20.4 13 20.4s13-11 13-20.4C27 6.6 21.2.8 14 .8Z" fill="${fill}" stroke="white" stroke-width="1.4"/>
    <circle cx="14" cy="13.4" r="8.4" fill="white"/>
    <text x="14" y="17" text-anchor="middle" font-family="-apple-system,sans-serif" font-size="10.5" font-weight="700" fill="${fill}">${n}</text>
  </svg>`;
  const src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  return new window.kakao.maps.MarkerImage(src, new window.kakao.maps.Size(w, h), {
    offset: new window.kakao.maps.Point(w / 2, h),
  });
}

export function PlaceMapView({ places: initialPlaces = [], referencePoint, variant = 'inline', selectedPlaceId, onSelect }: PlaceMapViewProps) {
  const toast = useToast();
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<kakao.maps.Map | null>(null);
  const markersRef = useRef<Array<{ id: string; marker: kakao.maps.Marker }>>([]);
  const myLocationOverlayRef = useRef<kakao.maps.CustomOverlay | null>(null);
  const appliedReferenceKeyRef = useRef<string | null>(null);
  const sheetDragStartYRef = useRef<number | null>(null);

  const [mapReady, setMapReady] = useState(false);
  const [mapError, setMapError] = useState<string | null>(null);
  const [queryText, setQueryText] = useState('');
  const [submittedQuery, setSubmittedQuery] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [searchAnchor, setSearchAnchor] = useState<ReferencePoint | null>(referencePoint ?? null);
  const [mapMovedAfterSearch, setMapMovedAfterSearch] = useState(false);
  const [sheetExpanded, setSheetExpanded] = useState(false);
  const [locating, setLocating] = useState(false);

  const searchInputId = useId();
  const isFullscreen = variant === 'fullscreen';
  const referenceKey = referencePoint ? `${referencePoint.x}:${referencePoint.y}` : '';

  // Sync parent selection so the list can return when the bottom card is cancelled.
  useEffect(() => {
    setSelectedId(selectedPlaceId ?? null);
  }, [selectedPlaceId]);

  // Load SDK once
  useEffect(() => {
    let cancelled = false;
    loadKakaoMap()
      .then(() => {
        if (!cancelled) setMapReady(true);
      })
      .catch(() => {
        if (!cancelled) setMapError('카카오 지도를 불러오지 못했습니다.');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Initialize map after SDK is ready
  useEffect(() => {
    if (!mapReady || !mapContainerRef.current || mapRef.current) return;
    const mapCenter = referencePoint ?? SEOUL_CENTER;
    const center = new window.kakao.maps.LatLng(mapCenter.y, mapCenter.x);
    const map = new window.kakao.maps.Map(mapContainerRef.current, {
      center,
      level: 5,
    });
    mapRef.current = map;
    window.kakao.maps.event.addListener(map, 'dragend', () => setMapMovedAfterSearch(true));
    window.kakao.maps.event.addListener(map, 'zoom_changed', () => setMapMovedAfterSearch(true));
    window.kakao.maps.event.addListener(map, 'click', () => setSheetExpanded(false));
  }, [mapReady]);

  // Center on the meeting point only when the actual reference coordinates change.
  // Parent rerenders after selecting a place create a new object with the same coordinates;
  // do not treat that as a new anchor or the map jumps back from the selected marker.
  useEffect(() => {
    if (!mapReady || !mapRef.current || !referencePoint || !referenceKey) return;
    if (appliedReferenceKeyRef.current === referenceKey) return;
    appliedReferenceKeyRef.current = referenceKey;
    mapRef.current.setCenter(new window.kakao.maps.LatLng(referencePoint.y, referencePoint.x));
  }, [mapReady, referenceKey, referencePoint]);

  const searchOrigin = searchAnchor ?? referencePoint;
  // Place search via react-query
  const {
    data: searchResults,
    isLoading: isSearching,
    isError: isSearchError,
    refetch: retrySearch,
  } = useQuery({
    queryKey: ['places', 'search', submittedQuery, searchOrigin?.x, searchOrigin?.y],
    queryFn: () => {
      const quick = QUICK_SEARCH_CHIPS.find((chip) => chip.label === submittedQuery);
      if (quick && searchOrigin) {
        const params = {
          x: searchOrigin.x,
          y: searchOrigin.y,
          radius: 2000,
          sort: 'distance' as const,
          ...('category' in quick ? { category: quick.category } : {}),
          ...('keyword' in quick ? { keyword: quick.keyword } : {}),
        };
        return searchNearby(params);
      }
      return searchPlaces(
        submittedQuery,
        searchOrigin ? String(searchOrigin.x) : undefined,
        searchOrigin ? String(searchOrigin.y) : undefined,
        searchOrigin ? 'distance' : undefined,
      );
    },
    enabled: submittedQuery.length > 0,
    staleTime: 60_000,
  });

  const isLoadingPlaces = isSearching;

  // Active list: keyword search > initial places. 지도 마커와 리스트 번호가 같은 순서.
  const activePlaces: PlaceResult[] = (
    submittedQuery.length > 0 ? (searchResults ?? []) : initialPlaces
  ).slice(0, MAX_RESULTS);
  const activePlacesKey = activePlaces.map((place) => place.id).join('|');

  // Sync numbered markers for every result (paging 없음 — 카카오맵처럼 전체 마커)
  useEffect(() => {
    if (!mapReady || !mapRef.current) return;

    markersRef.current.forEach(({ marker }) => marker.setMap(null));
    markersRef.current = [];
    if (activePlaces.length === 0) return;

    const map = mapRef.current;
    activePlaces.forEach((place, index) => {
      const position = new window.kakao.maps.LatLng(place.y, place.x);
      const marker = new window.kakao.maps.Marker({
        position,
        map,
        title: place.name,
        image: markerImage(index + 1, place.id === selectedId),
        zIndex: place.id === selectedId ? 30 : 10,
      });
      markersRef.current.push({ id: place.id, marker });
      window.kakao.maps.event.addListener(marker, 'click', () => {
        handleSelect(place, map);
      });
    });
    // Keep search context anchored to the meeting point; selecting a place pans intentionally.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePlacesKey, mapReady]);

  // Highlight the selected marker without rebuilding the whole set
  useEffect(() => {
    if (!mapReady) return;
    markersRef.current.forEach(({ id, marker }, index) => {
      marker.setImage(markerImage(index + 1, id === selectedId));
      marker.setZIndex(id === selectedId ? 30 : 10);
    });
  }, [selectedId, mapReady]);

  function handleSelect(place: PlaceResult, map?: kakao.maps.Map) {
    setSelectedId(place.id);
    setSheetExpanded(false);

    const targetMap = map ?? mapRef.current;
    if (targetMap) {
      targetMap.panTo(new window.kakao.maps.LatLng(place.y, place.x));
    }
    onSelect(place);
  }

  function currentMapCenter(): ReferencePoint {
    const center = mapRef.current?.getCenter();
    if (!center) return referencePoint ?? { name: '현재 지도 위치', ...SEOUL_CENTER };
    return {
      name: '현재 지도 위치',
      x: center.getLng(),
      y: center.getLat(),
    };
  }

  function handleSearchSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = queryText.trim();
    if (!trimmed) return;
    setSearchAnchor(currentMapCenter());
    setMapMovedAfterSearch(false);
    setSheetExpanded(false);
    setSubmittedQuery(trimmed);
  }

  function handleQuickSearch(keyword: string) {
    setQueryText(keyword);
    setSearchAnchor(currentMapCenter());
    setMapMovedAfterSearch(false);
    setSubmittedQuery(keyword);
    setSheetExpanded(false);
  }

  // 검색 전이면 '맛집'을 기본 검색어로 실행 — 검색창·칩에 그대로 반영해
  // 무엇을 검색했는지 보이게 하고, 칩으로 바로 바꿀 수 있게 한다.
  function searchThisArea() {
    setSearchAnchor(currentMapCenter());
    setMapMovedAfterSearch(false);
    setSheetExpanded(false);
    if (!submittedQuery) {
      setQueryText('맛집');
      setSubmittedQuery('맛집');
    }
  }

  function moveToMyLocation() {
    const map = mapRef.current;
    if (!map) return;
    if (!('geolocation' in navigator)) {
      toast.error('이 기기에서 위치를 사용할 수 없어요.');
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocating(false);
        const position = new window.kakao.maps.LatLng(pos.coords.latitude, pos.coords.longitude);
        map.setLevel(4);
        map.panTo(position);
        setMapMovedAfterSearch(true);
        if (!myLocationOverlayRef.current) {
          const dot = document.createElement('div');
          dot.className = 'h-3.5 w-3.5 rounded-full border-2 border-white bg-primary shadow-card';
          dot.setAttribute('aria-hidden', 'true');
          myLocationOverlayRef.current = new window.kakao.maps.CustomOverlay({
            position,
            content: dot,
            zIndex: 5,
          });
        }
        myLocationOverlayRef.current.setPosition(position);
        myLocationOverlayRef.current.setMap(map);
      },
      () => {
        setLocating(false);
        toast.error('현재 위치를 가져오지 못했어요.');
      },
      { enableHighAccuracy: true, timeout: 8000 },
    );
  }

  function handleSheetPointerDown(e: React.PointerEvent<HTMLButtonElement>) {
    sheetDragStartYRef.current = e.clientY;
    e.currentTarget.setPointerCapture(e.pointerId);
  }

  function handleSheetPointerMove(e: React.PointerEvent<HTMLButtonElement>) {
    const startY = sheetDragStartYRef.current;
    if (startY == null) return;
    const deltaY = e.clientY - startY;
    if (deltaY < -28) setSheetExpanded(true);
    if (deltaY > 28) setSheetExpanded(false);
  }

  function handleSheetPointerUp(e: React.PointerEvent<HTMLButtonElement>) {
    const startY = sheetDragStartYRef.current;
    sheetDragStartYRef.current = null;
    e.currentTarget.releasePointerCapture(e.pointerId);
    if (startY == null) return;
    const deltaY = e.clientY - startY;
    if (Math.abs(deltaY) < 8) {
      setSheetExpanded((expanded) => !expanded);
    } else if (deltaY < 0) {
      setSheetExpanded(true);
    } else {
      setSheetExpanded(false);
    }
  }

  function kindOf(place: PlaceResult): string {
    const haystack = `${place.name} ${place.category}`.toLowerCase();
    if (/(보드게임|방탈출|노래방|볼링|pc방|피시방|만화카페|영화|공연|전시|문화|스포츠)/i.test(haystack)) {
      return '놀거리';
    }
    if (haystack.includes('음식점') || haystack.includes('식당') || haystack.includes('술집')) return '음식점';
    if (haystack.includes('카페') || haystack.includes('디저트') || haystack.includes('베이커리')) return '카페';
    return '장소';
  }

  const canSearchThisArea = isFullscreen && mapMovedAfterSearch;
  const listVisible = activePlaces.length > 0 && !(isFullscreen && selectedId);
  const rootClassName = isFullscreen
    ? 'relative h-full min-h-0 overflow-hidden bg-canvas'
    : 'flex flex-col gap-3';
  const controlsClassName = isFullscreen
    ? 'absolute inset-x-3 top-3 z-20 rounded-[18px] border border-surface-border/80 bg-canvas/92 p-2 shadow-sheet backdrop-blur-xl'
    : 'contents';
  const mapFrameClassName = isFullscreen
    ? 'absolute inset-0 overflow-hidden bg-surface'
    : 'relative overflow-hidden rounded-[22px] border border-surface-border/70 bg-surface/60 backdrop-blur-sm';
  const mapClassName = isFullscreen ? 'h-full w-full bg-surface-muted' : 'h-[170px] w-full bg-surface-muted';
  const feedbackClassName = isFullscreen ? 'absolute left-3 right-3 top-[178px] z-20' : 'contents';
  const resultClassName = isFullscreen
    ? `absolute inset-x-3 bottom-[max(env(safe-area-inset-bottom),12px)] z-20 flex flex-col overflow-hidden rounded-[22px] border border-surface-border/80 bg-canvas p-2 shadow-sheet transition-[max-height] duration-200 ${sheetExpanded ? 'max-h-[60dvh]' : 'max-h-[196px]'}`
    : 'space-y-2';

  return (
    <div className={rootClassName}>
      <div className={controlsClassName}>
      <form onSubmit={handleSearchSubmit} className="relative">
        <Input
          id={searchInputId}
          aria-label="장소 검색"
          placeholder="장소명이나 지역을 검색"
          value={queryText}
          onChange={(e) => setQueryText(e.target.value)}
          className="rounded-[14px] pr-16"
        />
        <button
          type="submit"
          disabled={isSearching || queryText.trim().length === 0}
          className="pressable absolute right-1.5 top-1.5 inline-flex h-[36px] items-center justify-center rounded-[12px] bg-primary px-3 text-[12px] font-semibold text-white transition disabled:opacity-40 focus-visible:outline-none focus-visible:shadow-focus-ring"
        >
          검색
        </button>
      </form>
        {isFullscreen ? (
          <div className="-mx-1 mt-2 overflow-x-auto pb-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <div className="flex min-w-max gap-1.5 px-1">
              {QUICK_SEARCH_CHIPS.map((chip) => (
                <button
                  key={chip.label}
                  type="button"
                  onClick={() => handleQuickSearch(chip.label)}
                  className={`pressable rounded-full px-3 py-1.5 text-[12px] font-semibold transition focus-visible:outline-none focus-visible:shadow-focus-ring ${
                    submittedQuery === chip.label
                      ? 'bg-primary text-white shadow-soft'
                      : 'bg-surface-muted text-ink-muted hover:bg-primary-soft hover:text-primary'
                  }`}
                >
                  {chip.label}
                </button>
              ))}
            </div>
          </div>
        ) : null}
      </div>
      {canSearchThisArea ? (
        <button
          type="button"
          onClick={searchThisArea}
          className="pressable absolute left-1/2 top-[104px] z-30 inline-flex -translate-x-1/2 items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-[12px] font-semibold text-white shadow-sheet focus-visible:outline-none focus-visible:shadow-focus-ring"
        >
          <Icon name="target" size={13} />
          {submittedQuery ? '이 지역에서 다시 검색' : '이 지역에서 검색'}
        </button>
      ) : null}
      {isFullscreen && !sheetExpanded ? (
        <button
          type="button"
          onClick={moveToMyLocation}
          disabled={locating}
          aria-label="내 위치로 이동"
          className={`pressable absolute right-3 z-30 flex h-11 w-11 items-center justify-center rounded-full border border-surface-border/80 bg-canvas/95 text-ink shadow-sheet backdrop-blur-xl transition disabled:opacity-60 focus-visible:outline-none focus-visible:shadow-focus-ring ${
            listVisible
              ? 'bottom-[calc(max(env(safe-area-inset-bottom),12px)+208px)]'
              : 'bottom-[calc(max(env(safe-area-inset-bottom),12px)+16px)]'
          }`}
        >
          {locating ? <LoadingSpinner /> : <Icon name="target" size={19} />}
        </button>
      ) : null}

      <div className={mapFrameClassName}>
        <div
          ref={mapContainerRef}
          className={mapClassName}
          aria-label="카카오 지도"
          role="img"
        />
        {mapError ? (
          <div className={`absolute inset-0 flex flex-col items-center justify-center gap-2 px-4 text-center ${isFullscreen ? 'bg-canvas/86 pt-[220px] pb-[42dvh] backdrop-blur-[2px]' : 'bg-error-soft'}`}>
            <p className="text-[13px] font-semibold text-error">지도 로딩 실패</p>
            <p className="text-[12px] font-medium text-error/80">
              {isFullscreen ? '지도 없이도 검색 결과를 추가할 수 있어요.' : mapError}
            </p>
            <button
              type="button"
              onClick={() => {
                setMapError(null);
                setMapReady(false);
                loadKakaoMap()
                  .then(() => setMapReady(true))
                  .catch(() => setMapError('카카오 지도를 불러오지 못했습니다.'));
              }}
              className="mt-1 rounded-xl bg-surface px-3 py-2 text-[12px] font-semibold text-error shadow-card focus-visible:outline-none focus-visible:shadow-focus-ring"
            >
              다시 시도
            </button>
          </div>
        ) : !mapReady ? (
          <div className="absolute inset-0 flex items-center justify-center bg-surface/80">
            <LoadingSpinner label="지도 로딩 중…" />
          </div>
        ) : null}
      </div>

      <div className={feedbackClassName}>
      {/* Search state feedback */}
      {isLoadingPlaces && (
        <div className="flex justify-center py-2">
          <LoadingSpinner label="장소 검색 중…" />
        </div>
      )}
      {isSearchError && (
        <ErrorState
          title="검색 오류"
          description="장소 검색에 실패했습니다. 다시 시도해주세요."
          onRetry={() => void retrySearch()}
          retryLabel="재검색"
        />
      )}
      </div>

      {/* Results list */}
      {listVisible && (
        <div className={resultClassName}>
          {isFullscreen ? (
            <button
              type="button"
              aria-label={sheetExpanded ? '검색 결과 접기' : '검색 결과 펼치기'}
              onPointerDown={handleSheetPointerDown}
              onPointerMove={handleSheetPointerMove}
              onPointerUp={handleSheetPointerUp}
              onPointerCancel={() => {
                sheetDragStartYRef.current = null;
              }}
              className="mb-1 flex w-full shrink-0 touch-none flex-col items-center rounded-xl py-1 text-[11px] font-medium text-ink-faint focus-visible:outline-none focus-visible:shadow-focus-ring"
            >
              <span className="h-1.5 w-10 rounded-full bg-surface-border" aria-hidden="true" />
            </button>
          ) : null}
          <div className="flex shrink-0 items-center justify-between px-1 pb-1">
            <span className="text-[13px] font-semibold text-ink">
              {submittedQuery ? `검색 결과 ${activePlaces.length}` : `등록된 후보 ${activePlaces.length}`}
            </span>
            {submittedQuery && searchOrigin ? (
              <span className="text-[11px] font-medium text-ink-faint">가까운 순</span>
            ) : null}
          </div>
          <ul
            aria-label="장소 검색 결과"
            className="min-h-0 flex-1 divide-y divide-surface-border/70 overflow-y-auto overscroll-contain"
          >
            {activePlaces.map((place, index) => {
              const distance = formatDistance(place.distanceM);
              const kind = kindOf(place);
              const address = place.roadAddress || place.address;
              return (
                <li key={place.id}>
                  <button
                    type="button"
                    onClick={() => handleSelect(place)}
                    className={`w-full px-1 py-2.5 text-left transition-colors duration-150 ease-airbnb focus-visible:outline-none focus-visible:shadow-focus-ring ${
                      selectedId === place.id ? 'bg-primary-soft' : 'hover:bg-surface-subtle'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span
                        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary-soft text-[12px] font-bold text-primary"
                        aria-hidden="true"
                      >
                        {index + 1}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <p className="truncate text-[15px] font-semibold leading-5 text-ink">{place.name}</p>
                          <span className="shrink-0 text-[12px] font-medium text-ink-muted">
                            {distance}
                          </span>
                        </div>
                        <p className="mt-0.5 flex items-center gap-1.5 text-[12px] text-ink-muted">
                          {place.rating != null ? (
                            <StarRating rating={place.rating} count={place.userRatingCount} />
                          ) : null}
                          <span className="truncate">
                            {kind}
                            {address ? ` · ${address}` : ''}
                          </span>
                        </p>
                      </div>
                      <span className="shrink-0 rounded-full bg-primary px-3 py-1.5 text-[12px] font-semibold text-white">
                        추가
                      </span>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {submittedQuery.length > 0 &&
        !isLoadingPlaces &&
        !isSearchError &&
        activePlaces.length === 0 && (
          <p className={`${isFullscreen ? 'absolute inset-x-3 bottom-[max(env(safe-area-inset-bottom),12px)] z-20 rounded-[18px] border border-surface-border/80 bg-canvas px-4 py-3 shadow-sheet' : 'py-4'} text-center text-body text-ink-muted`}>
            검색 결과가 없습니다.
          </p>
        )}
    </div>
  );
}
